import {compact, lastIndexOf, reduce, reverse, trimEnd} from 'lodash';
import {IOsUser} from 'types/DiplomatServiceProperties';
import {IGeoIpResponse} from 'types/GeoIpResponse';
import {
   FILE_SEPARATOR_REGXP,
   ROOT_FOLDER_REGEXP,
   UNC_REGXP,
   UNIX_SEP,
   WINDOWS_SEP,
   WIN_LETTER_ROOT_REGEXP,
} from 'common/constants';
import escapeFile from 'sanitize-filename';

export const baseCollator = new Intl.Collator(undefined, {numeric: true, sensitivity: 'base'});
export const baseNameComparator = (a: {name: string}, b: {name: string}) =>
   baseCollator.compare(a.name, b.name);

export const transferListComparator = (freeze: string[]) => (
   a: {name: string},
   b: {name: string}
) => {
   if (freeze.find((f) => a.name === f)) return -1;
   if (freeze.find((f) => b.name === f)) return 1;
   return baseCollator.compare(a.name, b.name);
};

type FolderToCompare = {name: string; level?: number; comparisonText?: string};

export const alphaNumericSortBy = <T extends FolderToCompare>(
   collection: T[],
   compareIter: (keyof FolderToCompare)[]
) => {
   const items = [...collection];
   return items.sort((a, b) => {
      let result = 0;
      for (let cI of compareIter) {
         result = baseCollator.compare(String(a[cI]), String(b[cI]));
         if (result === 0) continue;
         return result;
      }
      return result;
   });
};

export const getOsUserString = (user: IOsUser) => {
   return [user.domain, user.userID].join('/');
};

export const getGeoDisplay = (geo: IGeoIpResponse) => {
   let sb = geo.countryName || geo.countryCode;
   if (sb) {
      sb += `, ${[geo.regionName || geo.regionCode, geo.city, geo.postalCode].join(',')}`;
      sb += geo.ip ? `, (${geo.ip})` : '';
   }

   return sb;
};

export const formatSerialNumber = (serial: string) =>
   reduce(serial, (result, val, i) => {
      return i % 4 === 0 ? result + '-' + val : result + val;
   });

export const isObfuscated = (pass: string) => {
   return Boolean(pass && pass.replace(/\*/g, '').length <= 0);
};

export const DEFAULT_PATH = '';
export const UPFOLDER_NAME = '..';
export function getArrayOfBreadcrumbs(path: string = '', rootFolderName = UPFOLDER_NAME) {
   const res = [] as {bc: string; bcPath: string}[];

   const isUNC = UNC_REGXP().test(path.slice(0, 2));
   // console.debug('getArrayOfBreadcrumbs', isUNC);

   let fileSep = UNIX_SEP;
   for (let i = path.length - 1, currentPath = path; i >= 0; i--) {
      if (FILE_SEPARATOR_REGXP().test(path[i])) {
         fileSep = path[i]; // remember the separator of current system
         const bc = currentPath.slice(i === 0 ? i : i + 1, currentPath.length);

         bc &&
            res.push({
               bc,
               bcPath: currentPath,
            });
         // word = '';
         currentPath = currentPath.slice(0, i);
      } else if (!isUNC && i === 0) {
         // console.debug('isUnc', isUNC);
         res.push({
            bc: currentPath.slice(i === 0 ? i : i + 1, currentPath.length),
            bcPath: currentPath,
         });
      } else {
         // word += path[i];
      }
   }

   if (isUNC) {
      res.pop();
      res.pop();
   }

   const result = reverse(res);

   const isDriveLetter = WIN_LETTER_ROOT_REGEXP().test(path);
   if (isDriveLetter) {
      result[0].bcPath = result[0].bcPath + fileSep;
   }

   return result;
}

export const getRootFolderPath = (path: string = '') => {
   let rootPath = DEFAULT_PATH;
   // FILE_SEPARATOR_REGXP.lastIndex = 0;
   if (FILE_SEPARATOR_REGXP().test(path[0])) {
      rootPath = findSeparator(path);
   }
   return rootPath;
};

export const isRootFolderPath = (path?: string) => {
   if (!path) return true;
   // ROOT_FOLDER_REGEXP.lastIndex = 0;
   const isRootFolder = ROOT_FOLDER_REGEXP().test(path);
   console.debug('isRootFolder', path, isRootFolder);
   return isRootFolder;
};

export const getFileNameFromPath = (path: string) => {
   const sep = findSeparator(path);
   const lastSepIndex = lastIndexOf(path, sep);
   const fileName = path.slice(lastSepIndex + 1, path.length);

   return fileName;
};

export const trimPath = (path?: string) => {
   if (!path) return '';
   const sep = findSeparator(path);
   const result = trimEnd(path, sep);
   return result;
};

export const getFileNameFromPathWithoutExtension = (path: string) => {
   const filename = getFileNameFromPath(path);

   const lastDotIndex = lastIndexOf(filename, '.');

   console.log(
      "getFileNameFromPathWithoutExtension(%o) with filename '%o' and lastDotIndex '%d'",
      path,
      filename,
      lastDotIndex
   );
   const fileNameWithoutExtension = lastDotIndex >= 0 ? filename.slice(0, lastDotIndex) : filename;

   return fileNameWithoutExtension;
};

export const escapeFileName = (file: string) => escapeFile(file, {replacement: '_'});

export const truncatePath = (path: string, maxLength = 30) => {
   if (path.length > 30) {
      const tPath = path.slice(-(maxLength - 3));
      return '...' + tPath;
   }
   return path;
};

export const findSeparator = (path: string) => {
   let separator = UNIX_SEP;
   let match;
   // console.debug('separator index', FILE_SEPARATOR_REGXP.lastIndex);
   // FILE_SEPARATOR_REGXP.lastIndex = 0;
   if ((match = path.match(FILE_SEPARATOR_REGXP()))) {
      separator = match[2] ? WINDOWS_SEP : UNIX_SEP;
   }
   // console.debug('separator', separator);
   // FILE_SEPARATOR_REGXP.lastIndex = 0;
   return separator;
};

// TODO try to use separator from path
export const concatPath = (path1: string, path2: string) => {
   const lastIndex = path1.length - 1;
   const separator = findSeparator(path1);

   if (path1.lastIndexOf(WINDOWS_SEP) === lastIndex || path1.lastIndexOf(UNIX_SEP) === lastIndex) {
      return path1 + path2;
   }
   return path1 + separator + path2;
};

export const replaceAt = (str: string, index: number, char: string) => {
   var a = str.split('');
   a[index] = char;
   const result = a.join('');
   // console.debug('replaceAt', str, index, char, result);
   return result;
};

export const joinAnd = (_words: (string | undefined)[]) => {
   if (_words.length > 1) {
      const words = compact(_words);
      const lastWord = words.pop();
      return [words.join(', '), lastWord].join(' and ');
   }
   return _words[0] || _words;
};
